import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Result, Table, Tag, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import agentTokenService, { IAgentToken, IGenerateTokenResponse } from '../../../services/aiChat/agentToken.service';
import GenerateTokenModal from './components/GenerateTokenModal';
import TokenCreatedModal from './components/TokenCreatedModal';
import './agentTokens.less';

const { Text } = Typography;

const formatDate = (iso: string | null): string => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const isExpiringSoon = (iso: string): boolean => {
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 && ms < 30 * 24 * 60 * 60 * 1000;
};

const AgentTokens: React.FC = () => {
  const [tokens, setTokens] = useState<IAgentToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [generateVisible, setGenerateVisible] = useState(false);
  const [createdToken, setCreatedToken] = useState<IGenerateTokenResponse | null>(null);
  const [revokeLoadingId, setRevokeLoadingId] = useState<number | null>(null);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentTokenService.list();
      setTokens(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      }
      // Other errors are handled by the request interceptor (toast)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleRevoke = (token: IAgentToken) => {
    Modal.confirm({
      title: `Revoke "${token.label}"?`,
      icon: <ExclamationCircleOutlined />,
      content: `The agent using this token will immediately lose access.`,
      okText: 'Revoke',
      okButtonProps: { danger: true, loading: revokeLoadingId === token.id },
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        setRevokeLoadingId(token.id);
        try {
          await agentTokenService.revoke(token.id);
          setTokens((prev) =>
            prev.map((t) => (t.id === token.id ? { ...t, is_active: false } : t))
          );
          toast.success(`Token "${token.label}" has been revoked.`);
        } catch {
          toast.error('Failed to revoke token. Please try again.');
        } finally {
          setRevokeLoadingId(null);
        }
      },
    });
  };

  const handleGenerateSuccess = (result: IGenerateTokenResponse) => {
    setGenerateVisible(false);
    setCreatedToken(result);
  };

  const handleTokenCreatedDone = () => {
    setCreatedToken(null);
    loadTokens();
  };

  const columns: ColumnsType<IAgentToken> = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (val: string) => <Text strong>{val}</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) =>
        record.is_active ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Revoked</Tag>
        ),
    },
    {
      title: 'Created',
      key: 'created_at',
      width: 130,
      render: (_, record) => formatDate(record.created_at),
    },
    {
      title: 'Expires',
      key: 'expires_at',
      width: 130,
      render: (_, record) => {
        const label = formatDate(record.expires_at);
        return isExpiringSoon(record.expires_at) ? (
          <Tooltip title="Expires within 30 days">
            <span className="agent-tokens-expiring-soon">{label}</span>
          </Tooltip>
        ) : (
          label
        );
      },
    },
    {
      title: 'Last Used',
      key: 'last_used_at',
      width: 130,
      render: (_, record) => formatDate(record.last_used_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) =>
        record.is_active ? (
          <Button
            size="small"
            danger
            loading={revokeLoadingId === record.id}
            onClick={() => handleRevoke(record)}
          >
            Revoke
          </Button>
        ) : null,
    },
  ];

  if (accessDenied) {
    return (
      <div className="agent-tokens-access-denied">
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have access to this page. Global Admin role is required."
        />
      </div>
    );
  }

  return (
    <div className="agent-tokens-page">
      <div className="agent-tokens-header">
        <Typography.Title level={4} className="agent-tokens-title">
          Agent Tokens
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setGenerateVisible(true)}
        >
          Generate Token
        </Button>
      </div>

      <Table<IAgentToken>
        rowKey="id"
        columns={columns}
        dataSource={tokens}
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No agent tokens have been generated yet.' }}
        className="agent-tokens-table"
      />

      <GenerateTokenModal
        visible={generateVisible}
        onCancel={() => setGenerateVisible(false)}
        onSuccess={handleGenerateSuccess}
      />

      <TokenCreatedModal data={createdToken} onDone={handleTokenCreatedDone} />
    </div>
  );
};

export default AgentTokens;
